import { Subject } from 'rxjs';
import { XmlRuleElement } from './xml-rules-check/xmlRuleElement.class';
import { XmlEditorConfig } from './xmlEditorConfig';
import { XmlEditorDebugger } from './xmlEditorDebugger';
import { XmlCursorPosMover } from './cursor/xmlCursorPosMover.class';
import { XmlCursor } from './cursor/xmlCursor.class';
import { OnDestroy } from '@angular/core';
import { XmlCursorPosSetter } from './cursor/xmlCursorPosSetter.class';
import { DomDummyNodeManager } from './dummyNodes/domDummyNodeManager.class';
import { XmlDomCleaner } from './xml-editing/xmlDomCleaner.class';
import { XmlEditDelete } from './xml-editing/xmlEditDeleteclass';
import { Xml2html } from './xml-html-converter/xml2html.class';
import { TouchSequence } from 'selenium-webdriver';
import { XmlEditInsert } from './xml-editing/xmlEditInsert.class';
import { debug } from 'util';

export class XmlEditorContext implements OnDestroy {
  public debugService: XmlEditorDebugger;
  public config: XmlEditorConfig;

  actualCursorChanged: Subject<XmlCursor>;
  actualCursor = new XmlCursor();
  dummyNodeManager = new DomDummyNodeManager();

  savedSelection: Range | null;
  selectedText: string;

  keyCtrlIsDown: boolean;
  keyShiftIsDown: boolean;

  constructor(private _document: Document) {
    this.actualCursorChanged = new Subject<XmlCursor>();
    this.debugService = new XmlEditorDebugger();
  }

  ngOnDestroy(): void {
    this.actualCursorChanged.unsubscribe();
  }

  setCursorPos(cursorPos: XmlCursor) {
    this.actualCursor = cursorPos;
    this.actualCursorChanged.next(cursorPos);
    this.debugService.setActualCursorPos(cursorPos);
  }

  // /**
  //   * Executed command from editor header buttons
  //   * @param command string from triggerCommand
  //   */
  // executeCommand(command: string) {
  //   if (command !== '') {
  //     this.commander.executeCommand(command);
  //     this.exec();
  //   }
  //   this.onEditorFocus();
  //   const cleanUpFirst = false;
  //   this.domDummyNodeManager.UpdateDummyNodes(this.xmlDocumentRootNode, cleanUpFirst);
  //   return;
  // }

  mouseUp(mouseEvent: MouseEvent) {
    if (window.getSelection) {
      let userSelection: Selection;
      userSelection = window.getSelection();
      XmlCursorPosSetter.setFromSelection(this.actualCursor.StartPos, userSelection.anchorNode, userSelection.anchorOffset, this.debugService);
      XmlCursorPosSetter.setFromSelection(this.actualCursor.EndPos, userSelection.focusNode, userSelection.focusOffset, this.debugService);
      this.setCursorPos(this.actualCursor);
      this.actualCursor.sortStartAndEnd();
      this.actualCursor.show();
    } else {
      throw new Error(`can't get window.getSelection, target=${JSON.stringify(mouseEvent.target)}`);
    }
  }

  keyDown(event: KeyboardEvent): boolean {
    console.log('keydown! key:' + event.key + ' / code:' + event.code);

    switch (event.key) {
      case 'Control':
        this.keyCtrlIsDown = true;
        break;

      case 'Shift':
        this.keyShiftIsDown = true;
        break;

      case 'c':
        if (this.keyCtrlIsDown) {
          // Copy
          return false;
        } else {
          if (this.insertText('c')) {
            return false;
          }
        }
        break;

      case 'ArrowUp':
      case 'ArrowDown':
        return true;

      case 'ArrowRight':
        if (this.keyShiftIsDown) {
          XmlCursorPosMover.moveRight(this.actualCursor.EndPos, this.actualCursor.xmlDocumentRootNode, this.config.rules);
        } else {
          XmlCursorPosMover.moveRight(this.actualCursor.EndPos, this.actualCursor.xmlDocumentRootNode, this.config.rules);
          this.actualCursor.StartPos.SetCursor(this.actualCursor.EndPos.ActualNode, this.actualCursor.EndPos.PosAtNode, this.actualCursor.EndPos.PosInTextnode);
        }
        this.actualCursor.sortStartAndEnd();
        this.actualCursor.show();
        this.setCursorPos(this.actualCursor);
        return false;

      case 'ArrowLeft':
        if (this.keyShiftIsDown) {
          XmlCursorPosMover.moveLeft(this.actualCursor.EndPos, this.actualCursor.xmlDocumentRootNode, this.config.rules);
        } else {
          XmlCursorPosMover.moveLeft(this.actualCursor.StartPos, this.actualCursor.xmlDocumentRootNode, this.config.rules);
          this.actualCursor.EndPos.SetCursor(this.actualCursor.StartPos.ActualNode, this.actualCursor.StartPos.PosAtNode, this.actualCursor.StartPos.PosInTextnode);
        }
        this.actualCursor.sortStartAndEnd();
        this.actualCursor.show();
        this.setCursorPos(this.actualCursor);
        return false;

      case 'Backspace':
        if (this.actualCursor.isEmptySelection()) {
          XmlEditDelete.deleteBefore(this.actualCursor.StartPos, this.actualCursor.xmlDocumentRootNode, this.config.rules);
        } else {
          XmlEditDelete.deleteSelection(this.actualCursor, this.actualCursor.xmlDocumentRootNode, this.config.rules);
        }
        this.actualCursor.EndPos.SetCursor(this.actualCursor.StartPos.ActualNode, this.actualCursor.StartPos.PosAtNode, this.actualCursor.StartPos.PosInTextnode);
        this.cleanUpAndUpdateDummyElements();
        this.actualCursor.show();
        return false;

      case 'Delete':
        if (this.actualCursor.isEmptySelection()) {
          XmlEditDelete.deleteAfter(this.actualCursor.StartPos, this.actualCursor.xmlDocumentRootNode, this.config.rules);
        } else {
          XmlEditDelete.deleteSelection(this.actualCursor, this.actualCursor.xmlDocumentRootNode, this.config.rules);
        }
        this.cleanUpAndUpdateDummyElements();
        this.actualCursor.show();
        return false;

      case 'Enter':
        return false;

      default:
        const textInsert = event.key;
        if (this.insertText(textInsert)) {
          return false;
        }
        break;
    }

    return true;
  }

  keyUp(event: KeyboardEvent): boolean {
    switch (event.key) {
      case 'Control':
        this.keyCtrlIsDown = false;
        break;
      case 'Shift':
        this.actualCursor.sortStartAndEnd();
        this.keyShiftIsDown = false;
        break;

      case 'ArrowLeft':
      case 'ArrowRight':
        this.actualCursor.show();
        break;

      case 'ArrowUp':
      case 'ArrowDown':
      case 'Backspace':
      case 'Delete':
      case 'Enter':
      case 'Control':
      case 'Shift':
        break;
    }

    return true;
  }

  pasted(text: string): boolean {
    alert('pasted: ' + text);
    return false;
  }

  copied(): boolean {
    alert('copied');
    return false;
  }

  insertText(value: string): boolean {
    if (value === '' || value === null || value === undefined) {
      return true;
    }

    let inserted = false;

    if (this.actualCursor.isEmptySelection()) {
      inserted = XmlEditInsert.insertText(this.actualCursor.EndPos, value, this.actualCursor.xmlDocumentRootNode, this.config.rules, this.config.xmlSchema);
    } else {
      inserted = XmlEditInsert.replaceWithText(this.actualCursor, value, this.actualCursor.xmlDocumentRootNode, this.config.rules, this.config.xmlSchema);
    }

    if (inserted) {
      // character inserted -> move cursor right
      // for (let index = 0; index < value.length; index++) {
      //   XmlCursorPosMover.moveRight(this.actualCursor.EndPos, this.actualCursor.xmlDocumentRootNode, this.config.rules);
      // }
      this.actualCursor.StartPos.SetCursor(this.actualCursor.EndPos.ActualNode, this.actualCursor.EndPos.PosAtNode, this.actualCursor.EndPos.PosInTextnode);
      this.cleanUpAndUpdateDummyElements();
      this.actualCursor.show();
      console.warn('text inserted!');
      return true;
    } else {
      console.warn('text NOT inserted!');
      return false;
    }
  }

  /** inserts a new element at the actual cursor pos */
  insertElement(element: XmlRuleElement) {
    const html = new Xml2html(this.config.rules).createNewElementHtml(element);
    this.domInsertHtml(html);
    this.cleanUpAndUpdateDummyElements();
    // this.actualCursor.show();
    console.warn('element inserted!');
  }

  /**
   * Create raw HTML
   * @param html HTML string
   */
  public domInsertHtml(html: string): void {
    if (document.getSelection) {
      const range = document.getSelection().getRangeAt(0);
      range.insertNode(range.createContextualFragment(html));
    } else {
      throw new Error('document.getSelection not defined!');
    }
    this.cleanUpAndUpdateDummyElements();
    this.actualCursor.show();
  }

  /**
   * save selection when the editor is focussed out
   */
  saveSelection(): any {
    if (window.getSelection) {
      const sel = window.getSelection();
      if (sel.getRangeAt && sel.rangeCount) {
        this.savedSelection = sel.getRangeAt(0);
        this.selectedText = sel.toString();
      }
    } else if (this._document.getSelection && this._document.createRange) {
      this.savedSelection = document.createRange();
    } else {
      this.savedSelection = null;
    }
  }

  /**
   * restore selection when the editor is focussed in
   */
  restoreSelection(): boolean {
    if (this.savedSelection) {
      if (window.getSelection) {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(this.savedSelection);
        return true;
      } else if (this._document.getSelection /*&& this.savedSelection.select*/) {
        // this.savedSelection.select();
        return true;
      }
    } else {
      return false;
    }
  }

  cleanUpAndUpdateDummyElements() {
    this.dummyNodeManager.RemoveAllDummyNodes(this.actualCursor.xmlDocumentRootNode);
    XmlDomCleaner.cleanUp(this.actualCursor.xmlDocumentRootNode, this.actualCursor);
    this.dummyNodeManager.UpdateDummyNodes(this.actualCursor.xmlDocumentRootNode);
    this.actualCursor.sortStartAndEnd();
  }

  // /** check any slection is made or not */
  // private checkSelection(): any {
  //   const slectedText = this.savedSelection.toString();

  //   if (slectedText.length === 0) {
  //     throw new Error('No Selection Made');
  //   }

  //   return true;
  // }

  // setDefaultParagraphSeparator(separator: string) {
  //   this._document.execCommand('defaultParagraphSeparator', false, separator);
  // }
}
